import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
  useBingoSpinMutation,
  useGetBingoEventQuery,
} from '@/api/queries/bingo';
import { AppRoute } from '@/common/app/routes';
import { Playdeck } from '@/common/lib/playdeck';
import { BingoNoBalanceModal } from '@/components/Bingo/BingoNoBalanceModal/BingoNoBalanceModal';
import { CounterUpDown } from '@/components/CounterUpDown/CounterUpDown';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H2 } from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { Loader } from '@/components/ui/Loader/Loader';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import {
  applySpinResults,
  toggleSkipAnimation,
} from '@/redux/reducers/bingoReducer';
import { setBingoBalls } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const BingoRollButtons: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalContext);
  const [spin] = useBingoSpinMutation();
  const { data: event } = useGetBingoEventQuery();
  const [freeSpinUsed, setFreeSpinUsed] = useState(false);
  const freeSpinAvailable =
    !freeSpinUsed && (event?.freeSpinAvailable ?? false);
  const isRolling = useAppSelector((state) => state.bingo.isRolling);
  const ballsBalance = useAppSelector(
    (state) => state.user.user?.balance?.bingoBalls ?? 0,
  );
  const skipAnimation = useAppSelector((state) => state.bingo.skipAnimation);
  const boardComplete = useAppSelector((state) => state.bingo.boardComplete);
  const allDone = useAppSelector(
    (state) =>
      state.bingo.tiles.filter((t) => t.kind === 'normal' && t.state === 'idle')
        .length === 0,
  );

  const [noBalanceOpen, setNoBalanceOpen] = useState(false);
  const [spinningCount, setSpinningCount] = useState<1 | 10 | 100 | null>(null);

  const handleRoll = async (count: 1 | 10 | 100): Promise<void> => {
    const isFreeSpin = count === 1 && freeSpinAvailable;

    if (!isFreeSpin && ballsBalance < count) {
      setNoBalanceOpen(true);

      return;
    }

    if (isRolling || spinningCount !== null || allDone || boardComplete) return;

    setSpinningCount(count);

    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'bingo_spin',
      user_properties: {},
      event_properties: {
        count,
        is_free_spin: isFreeSpin,
        balls_before: ballsBalance,
        skip_animation: skipAnimation,
        daily_contract_balls: event?.dailyContractBalls ?? 0,
        daily_contract_balls_limit: event?.dailyContractBallsLimit ?? 0,
      },
    });

    try {
      const result = await spin({ count }).unwrap();
      dispatch(setBingoBalls(result.bingoBalls));
      dispatch(applySpinResults(result));

      if (isFreeSpin) {
        setFreeSpinUsed(true);
      }
    } catch (err) {
      const error = err as { status?: number; data?: { message?: string } };

      if (error.status === 400 && error.data?.message) {
        showToast(error.data.message);
      } else {
        showToast('Something went wrong');
      }
    } finally {
      setSpinningCount(null);
    }
  };

  return (
    <>
      <BingoNoBalanceModal
        isOpen={noBalanceOpen}
        onClose={() => setNoBalanceOpen(false)}
      />

      <div className={css.wrapper}>
        {/* Balances row: Back | Ball counter | Skip animation */}
        <div className={css.balancesRow}>
          <button
            className={css.backBtn}
            onClick={() => navigate(AppRoute.MAIN)}
          >
            <img
              src="/img/icons/arrow-go.svg"
              alt=""
              className={css.backArrow}
            />
            <span className={css.backText}>{t('Office')}</span>
          </button>

          <div className={css.ballCounter}>
            <img
              src="/img/bingo/bingo-icon.png"
              alt=""
              className={css.ballIcon}
            />
            <CounterUpDown
              value={ballsBalance}
              delay={0.3}
              duration={0.6}
              className={css.ballValue}
            />
            <button
              className={css.addBtn}
              onClick={() =>
                openModal({
                  renderKind: 'buyBingoBallsModal',
                  props: { source: 'plus_button' },
                })
              }
            >
              <img
                src="/img/bingo/plus-icon.svg"
                alt=""
                className={css.addIcon}
              />
            </button>
          </div>

          <label className={css.skipLabel}>
            <input
              type="checkbox"
              checked={skipAnimation}
              onChange={() => dispatch(toggleSkipAnimation())}
              className={css.skipCheckbox}
            />
            <span className={css.skipCustom} data-checked={skipAnimation}>
              {skipAnimation && (
                <img
                  src="/img/bingo/arrow-go.svg"
                  alt=""
                  className={css.checkMark}
                />
              )}
            </span>
            <span className={css.skipText}>{t('Skip animation')}</span>
          </label>
        </div>

        {/* Roll buttons */}
        <div className={css.buttons}>
          {freeSpinAvailable ? (
            <Button
              variant="primary"
              onClick={() => handleRoll(1)}
              disabled={spinningCount !== null}
              className={css.rollBtn}
            >
              {spinningCount === 1 ? (
                <Loader size={24} />
              ) : (
                <H2
                  className={cn('main-btn-text-shadow', css.btnText)}
                  weight="extrabold"
                >
                  {t('Free')}
                </H2>
              )}
            </Button>
          ) : (
            ([1, 10] as const).map((count) => {
              const insufficient = ballsBalance < count;
              const isThisSpinning = spinningCount === count;
              const isDisabled = spinningCount !== null && !isThisSpinning;

              return (
                <Button
                  key={count}
                  variant="primary"
                  onClick={() => handleRoll(count)}
                  disabled={isDisabled}
                  className={css.rollBtn}
                >
                  {isThisSpinning ? (
                    <Loader size={24} />
                  ) : (
                    <H2
                      className={cn('main-btn-text-shadow', css.btnText)}
                      weight="extrabold"
                    >
                      {t('Open')}{' '}
                      <span className={cn(insufficient && css.countRed)}>
                        x{count}
                      </span>
                    </H2>
                  )}
                </Button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export { BingoRollButtons };
